import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { CheckBoxSelectionService } from '@syncfusion/ej2-angular-dropdowns'
import { debounceTime, filter, Subject, tap } from 'rxjs'
import { Client, ClientYear } from './../../../core/model/client.model'
import { Study } from './../../../core/model/study.model'
import { TierPath } from '../../tier.model'
import { ProgramsYearsFilter } from '../credit-tier.model'

@Component({
  selector: 'app-credit-tier-bar-multiselect',
  templateUrl: './credit-tier-bar-multiselect.component.html',
  styleUrls: ['./credit-tier-bar-multiselect.component.scss'],
  providers: [CheckBoxSelectionService],
})
export class CreditTierBarMultiselectComponent implements OnInit, OnChanges {
  private updateFilters$ = new Subject()
  private selectedYears: ClientYear[] = []
  private selectedPrograms: Study[] = []

  disabledYearId: string
  disabledProgramId: string
  selectedYearIds: string[] = []
  selectedProgramIds: string[] = []

  @Input() clients: Client[]
  @Input() years: ClientYear[]
  @Input() programs: Study[]
  @Input() selectedClientId: string
  @Input() yearsDisabled: boolean = false
  @Input() programsDisabled: boolean = false

  private _filters: ProgramsYearsFilter
  @Input() set filters(value: ProgramsYearsFilter) {
    this._filters = value
    this.selectedYears = value?.years ?? []
    this.selectedPrograms = value?.programs ?? []
    const newYearIds = this.selectedYears.map((y) => y.id)
    const newProgramIds = this.selectedPrograms.map((p) => p.id)
    if (!this.sameIds(newYearIds, this.selectedYearIds)) {
      this.selectedYearIds = newYearIds
    }
    if (!this.sameIds(newProgramIds, this.selectedProgramIds)) {
      this.selectedProgramIds = newProgramIds
    }
  }
  get filters() {
    return this._filters
  }

  private sameIds(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id) => b.includes(id))
  }

  @Output() tierChange = new EventEmitter<TierPath>()
  @Output() updateFilters = new EventEmitter<ProgramsYearsFilter>()

  public yearFields = { text: 'year', value: 'id', enabled: 'enabled' }
  public programFields = { text: 'name', value: 'id', enabled: 'enabled' }

  ngOnInit(): void {
    this.updateFilters$
      .pipe(
        filter(() => this.validFilters()),
        debounceTime(1500),
        tap(() => this.updateFilters.emit(this.getFilters())),
      )
      .subscribe()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']) {
      this.disabledProgramId =
        this.filters?.programs.length === 1 ? this.filters.programs[0].id : ''
      this.disabledYearId = this.filters?.years.length === 1 ? this.filters.years[0].id : ''
    }
  }

  onClientChange(client: string): void {
    if (client !== this.selectedClientId) {
      this.tierChange.emit({
        client,
        year: null,
        program: null,
        structure: null,
      })
    }
  }

  onYearChange(ids: string[]): void {
    if (!ids?.length && this.years?.length) return
    if (this.sameIds(ids ?? [], this.selectedYearIds)) return
    this.selectedYearIds = ids ?? []
    this.selectedYears = this.years?.filter((y) => ids?.includes(y.id)) ?? []
    this.updateFilters$.next('')
  }

  onProgramChange(ids: string[]): void {
    if (!ids?.length && this.programs?.length) return
    if (this.sameIds(ids ?? [], this.selectedProgramIds)) return
    this.selectedProgramIds = ids ?? []
    this.selectedPrograms = this.programs?.filter((p) => ids?.includes(p.id)) ?? []
    this.updateFilters$.next('')
  }

  private validFilters(): boolean {
    return this.selectedYears.length > 0 && this.selectedPrograms.length > 0
  }

  private getFilters(): ProgramsYearsFilter {
    return {
      years: this.selectedYears,
      programs: this.selectedPrograms,
    }
  }
}
